/**
 * Fantasy Points Page
 *
 * Displays detailed fantasy points breakdown for drivers and constructors
 * for each race in the available fantasy seasons (2023+).
 *
 * Uses SSR streaming pattern:
 * - Seasons and races are awaited in loader (fast, needed for selectors)
 * - Fantasy results load client-side with skeleton fallback
 */

import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  Clock,
  Flag,
  HelpCircle,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { m } from "@/paraglide/messages";
import { DEFAULT_SEASON } from "@/tools/f1-fantasy/lib/constants";
import { getNextOrLatestRace } from "@/tools/f1-fantasy/lib/race-utils";

const DriverFantasyTable = lazy(() =>
  import("../components/DriverFantasyTable").then((m) => ({
    default: m.DriverFantasyTable,
  })),
);
const ConstructorFantasyTable = lazy(() =>
  import("../components/ConstructorFantasyTable").then((m) => ({
    default: m.ConstructorFantasyTable,
  })),
);

import {
  FantasyErrorAlert,
  FantasyPointsErrorBoundary,
} from "../components/FantasyPointsErrorBoundary";
import {
  ConstructorFantasyTableSkeleton,
  DriverFantasyTableSkeleton,
  RaceInfoSkeleton,
} from "../components/FantasyPointsSkeletons";
import { SeasonRaceSelector } from "../components/SeasonRaceSelector";
import {
  type ConstructorFantasyResult,
  type DriverFantasyResult,
  type Race,
  useConstructorFantasyResults,
  useDriverFantasyResults,
  useRaces,
  useSeasons,
} from "../hooks/use-f1-data";
import { calculateConstructorFantasyBreakdown } from "../lib/constructor-scoring";
import {
  getQualifyingPoints,
  getRacePoints,
  getSprintPoints,
} from "../lib/fantasy-points";
import { getScoringRules } from "../lib/scoring-rules";
import { getTeamColor } from "../lib/team-colors";

// Fantasy data is only available for seasons >= 2023
const MIN_FANTASY_SEASON = 2023;

// Route API for accessing loader data
const routeApi = getRouteApi("/f1-fantasy/fantasy-points");

export function FantasyPoints() {
  // Get SSR-prefetched loader data for initial state
  const loaderData = routeApi.useLoaderData();

  // Read URL search params — source of truth for selected season/race
  const { season: urlSeason, race: urlRaceSlug } = routeApi.useSearch();
  const navigate = useNavigate({ from: "/f1-fantasy/fantasy-points" });

  // Fetch seasons to get the list for filtering
  const { data: allSeasons } = useSeasons();

  // Filter to only fantasy seasons (>= 2023)
  const fantasySeasons = useMemo(() => {
    if (!allSeasons) return [];
    return allSeasons
      .filter((s) => s.year >= MIN_FANTASY_SEASON)
      .map((s) => s.year);
  }, [allSeasons]);

  // Season/race state — seeded from URL params (with loader data as SSR fallback)
  const [selectedSeason, setSelectedSeason] = useState<number | null>(
    urlSeason ?? loaderData.season,
  );
  const [selectedRaceSlug, setSelectedRaceSlug] = useState<string | null>(
    urlRaceSlug ?? loaderData.raceSlug,
  );

  // Initialize season when fantasy seasons load (render-time state adjustment)
  if (!selectedSeason && fantasySeasons.length > 0) {
    setSelectedSeason(fantasySeasons[0]);
  }

  // Fetch races (SSR prefetched for loader season)
  const { data: races } = useRaces(selectedSeason ?? undefined);

  // Derive selectedRace from races data using the slug
  const selectedRace = useMemo<Race | null>(() => {
    if (!races || !selectedRaceSlug) return null;
    return races.find((r) => r.slug === selectedRaceSlug) ?? null;
  }, [races, selectedRaceSlug]);

  // Last completed race for data freshness indicator
  // Capture current time once at mount — stable reference for SSR/client hydration
  const [now] = useState(() => Date.now());
  const lastRace = useMemo(() => {
    if (!races) return null;
    const past = races.filter(
      (r) => r.date && new Date(r.date).getTime() < now,
    );
    return past.length > 0 ? past[past.length - 1] : null;
  }, [races, now]);

  // When races change for a non-loader season, select the latest completed race
  const [prevRaces, setPrevRaces] = useState(races);
  if (races !== prevRaces) {
    setPrevRaces(races);
    if (races && races.length > 0 && selectedSeason !== loaderData.season) {
      const now = new Date();
      const pastRaces = races.filter((r) => r.date && new Date(r.date) < now);
      const latestPast =
        pastRaces.length > 0 ? pastRaces[pastRaces.length - 1] : null;
      setSelectedRaceSlug(
        (latestPast ?? getNextOrLatestRace(races))?.slug ?? null,
      );
    }
  }

  // Handlers — update both React state and URL search params
  const handleSeasonChange = (season: number) => {
    setSelectedSeason(season);
    setSelectedRaceSlug(null); // clear so auto-pick kicks in for new season
    navigate({ search: { season }, replace: true });
  };

  const handleRaceChange = (race: Race) => {
    setSelectedRaceSlug(race.slug);
    navigate({
      search: (prev) => ({ ...prev, race: race.slug }),
      replace: true,
    });
  };

  // Fetch fantasy results (loads client-side with skeletons)
  const { data: driverResults, isLoading: isLoadingDrivers } =
    useDriverFantasyResults(
      selectedSeason ?? undefined,
      selectedRaceSlug ?? undefined,
    );

  const { data: constructorResults, isLoading: isLoadingConstructors } =
    useConstructorFantasyResults(
      selectedSeason ?? undefined,
      selectedRaceSlug ?? undefined,
    );

  // Show loading only on initial load, not on refetch
  const showLoading =
    (isLoadingDrivers || isLoadingConstructors) &&
    !driverResults &&
    !constructorResults;

  // Use the isSprintWeekend field from the selected race
  const isSprintWeekend = selectedRace?.isSprintWeekend ?? false;

  // Collect render errors from child components to show at top of page
  const [renderErrors, setRenderErrors] = useState<Error[]>([]);
  const addError = (error: Error) =>
    setRenderErrors((prev) =>
      prev.some((e) => e.message === error.message) ? prev : [...prev, error],
    );

  // Mobile state
  const [mobileEntity, setMobileEntity] = useState<"drivers" | "constructors">(
    "drivers",
  );

  // Desktop entity tab state
  const [desktopEntity, setDesktopEntity] = useState<
    "drivers" | "constructors"
  >("drivers");

  return (
    <div>
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4 space-y-2 md:space-y-0">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/f1-fantasy"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">{m.common_back()}</span>
              </Link>
              <div className="w-px h-6 bg-border" />
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                <h1 className="font-display text-xl font-bold text-foreground">
                  {m.f1_points_title()}
                </h1>
              </div>
            </div>

            {/* Desktop: Drivers/Constructors tab + Season/Race Selector */}
            <div className="hidden md:flex items-center gap-3">
              <div className="flex rounded-lg tab-group p-0.5">
                <button
                  type="button"
                  onClick={() => setDesktopEntity("drivers")}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    desktopEntity === "drivers"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  {m.f1_standings_tab_drivers()}
                </button>
                <button
                  type="button"
                  onClick={() => setDesktopEntity("constructors")}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    desktopEntity === "constructors"
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Flag className="h-4 w-4" />
                  {m.f1_standings_tab_constructors()}
                </button>
              </div>
              <SeasonRaceSelector
                defaultSeason={urlSeason ?? loaderData.season ?? undefined}
                initialRaceSlug={urlRaceSlug ?? loaderData.raceSlug}
                filterSeasons={fantasySeasons}
                onSeasonChange={handleSeasonChange}
                onRaceChange={handleRaceChange}
                showRaceSelector={true}
                filterRacesToSelectable
              />
            </div>
          </div>
          {/* Mobile-only selector rows */}
          <div className="md:hidden space-y-2">
            <SeasonRaceSelector
              defaultSeason={loaderData.season ?? undefined}
              initialRaceSlug={loaderData.raceSlug}
              filterSeasons={fantasySeasons}
              onSeasonChange={handleSeasonChange}
              onRaceChange={handleRaceChange}
              showRaceSelector={true}
              filterRacesToSelectable
              compact
            />
            {/* Drivers / Constructors tab */}
            <div className="flex rounded-lg tab-group p-0.5">
              <button
                type="button"
                onClick={() => setMobileEntity("drivers")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                  mobileEntity === "drivers"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Users className="h-4 w-4" />
                {m.f1_standings_tab_drivers()}
              </button>
              <button
                type="button"
                onClick={() => setMobileEntity("constructors")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                  mobileEntity === "constructors"
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Flag className="h-4 w-4" />
                {m.f1_standings_tab_constructors()}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Error alerts — shown at top when any child component throws */}
        <FantasyErrorAlert errors={renderErrors} />

        {/* Intro + Data Freshness */}
        <div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {m.f1_points_intro()}
          </p>
          {lastRace && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {m.f1_points_data_through({ name: lastRace.name })}
            </p>
          )}
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors mt-2"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                {m.f1_points_how_it_works()}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{m.f1_points_how_title()}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-foreground/80">
                <p>{m.f1_points_how_p1()}</p>
                <p>{m.f1_points_how_p2()}</p>
                <p>{m.f1_points_how_p3()}</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <AdPlacement className="my-6" />

        {/* Race Info */}
        {showLoading ? (
          <RaceInfoSkeleton />
        ) : (
          selectedRace && (
            <div className="text-center">
              <h2 className="font-display text-2xl font-bold">
                {m.f1_points_round({
                  round: selectedRace.round,
                  name: selectedRace.name,
                })}
              </h2>
              {isSprintWeekend && (
                <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">
                  <Zap className="h-3 w-3" />
                  {m.f1_apex_sprint_weekend()}
                </span>
              )}
            </div>
          )
        )}

        {/* Results - show skeletons while loading */}
        {showLoading ? (
          <>
            <DriverFantasyTableSkeleton isSprintWeekend={isSprintWeekend} />
            <ConstructorFantasyTableSkeleton
              isSprintWeekend={isSprintWeekend}
            />
          </>
        ) : (
          <>
            {/* ============================================================
						    MOBILE LAYOUT (below md:) — toggle + cards
						    ============================================================ */}
            <div className="md:hidden space-y-4 !mb-0">
              <MobileScoringLegend
                isDrivers={mobileEntity === "drivers"}
                isSprintWeekend={isSprintWeekend}
              />

              {mobileEntity === "drivers" &&
                driverResults &&
                driverResults.length > 0 && (
                  <FantasyPointsErrorBoundary onError={addError}>
                    <div className="space-y-3">
                      {driverResults.map((d) => (
                        <MobileDriverScoreCard
                          key={d.id}
                          driver={d}
                          isSprintWeekend={isSprintWeekend}
                          season={selectedSeason ?? DEFAULT_SEASON}
                        />
                      ))}
                    </div>
                  </FantasyPointsErrorBoundary>
                )}

              {mobileEntity === "constructors" &&
                constructorResults &&
                constructorResults.length > 0 && (
                  <FantasyPointsErrorBoundary onError={addError}>
                    <div className="space-y-3">
                      {constructorResults.map((c) => (
                        <MobileConstructorScoreCard
                          key={c.id}
                          constructor={c}
                          isSprintWeekend={isSprintWeekend}
                          season={selectedSeason ?? DEFAULT_SEASON}
                        />
                      ))}
                    </div>
                  </FantasyPointsErrorBoundary>
                )}
            </div>

            <AdPlacement className="my-6" />

            {/* ============================================================
						    DESKTOP LAYOUT (md: and above) — tabbed tables
						    ============================================================ */}
            <div className="hidden md:block space-y-8">
              <MobileScoringLegend
                isDrivers={desktopEntity === "drivers"}
                isSprintWeekend={isSprintWeekend}
              />

              {desktopEntity === "drivers" &&
                driverResults &&
                driverResults.length > 0 && (
                  <FantasyPointsErrorBoundary onError={addError}>
                    <Suspense
                      fallback={
                        <DriverFantasyTableSkeleton
                          isSprintWeekend={isSprintWeekend}
                        />
                      }
                    >
                      <DriverFantasyTable
                        data={driverResults}
                        isSprintWeekend={isSprintWeekend}
                        season={selectedSeason ?? DEFAULT_SEASON}
                      />
                    </Suspense>
                  </FantasyPointsErrorBoundary>
                )}

              {desktopEntity === "constructors" &&
                constructorResults &&
                constructorResults.length > 0 && (
                  <FantasyPointsErrorBoundary onError={addError}>
                    <Suspense
                      fallback={
                        <ConstructorFantasyTableSkeleton
                          isSprintWeekend={isSprintWeekend}
                        />
                      }
                    >
                      <ConstructorFantasyTable
                        data={constructorResults}
                        isSprintWeekend={isSprintWeekend}
                        season={selectedSeason ?? DEFAULT_SEASON}
                      />
                    </Suspense>
                  </FantasyPointsErrorBoundary>
                )}
            </div>

            {/* No Results */}
            {(!driverResults || driverResults.length === 0) &&
              (!constructorResults || constructorResults.length === 0) && (
                <div className="text-center py-16 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{m.f1_points_no_results()}</p>
                </div>
              )}
          </>
        )}
      </div>

      {/* Editorial content — the data tables alone left this page thin
          (~176 words), the last indexable page under the AdSense bar. */}
      <section className="max-w-3xl mx-auto px-5 py-10 space-y-8">
        <div className="space-y-3">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            {m.f1_fp_title()}
          </h2>
          {m
            .f1_fp_howto()
            .split("\n\n")
            .map((p) => (
              <p key={p} className="leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
        </div>
        <dl className="space-y-3">
          {[
            { q: m.f1_fp_faq1_q(), a: m.f1_fp_faq1_a() },
            { q: m.f1_fp_faq2_q(), a: m.f1_fp_faq2_a() },
            { q: m.f1_fp_faq3_q(), a: m.f1_fp_faq3_a() },
            { q: m.f1_fp_faq4_q(), a: m.f1_fp_faq4_a() },
          ].map((f) => (
            <div
              key={f.q}
              className="rounded-xl border border-border bg-card/40 p-4"
            >
              <dt className="font-semibold text-foreground">{f.q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </dd>
            </div>
          ))}
        </dl>
      </section>
      <AdPlacement className="mt-6" />
    </div>
  );
}

// ============================================================================
// Mobile Score Cards
// ============================================================================

function MobileScoringLegend({
  isDrivers,
  isSprintWeekend,
}: {
  isDrivers: boolean;
  isSprintWeekend: boolean;
}) {
  const [open, setOpen] = useState(false);

  const driverItems = [
    { abbr: "POS", desc: m.f1_points_legend_pos() },
    { abbr: "PG", desc: m.f1_points_legend_pg() },
    { abbr: "OV", desc: m.f1_points_legend_ov() },
    { abbr: "FL", desc: m.f1_points_legend_fl() },
    { abbr: "DD", desc: m.f1_points_legend_dd() },
  ];

  const constructorItems = [
    { abbr: "POS", desc: m.f1_points_legend_pos_combined() },
    { abbr: "TW", desc: m.f1_points_legend_tw() },
    { abbr: "DQ", desc: m.f1_points_legend_dq() },
    { abbr: "PG", desc: m.f1_points_legend_pg() },
    { abbr: "OV", desc: m.f1_points_legend_ov() },
    { abbr: "FL", desc: m.f1_points_legend_fl() },
    { abbr: "PT", desc: m.f1_points_legend_pt() },
    { abbr: "FP", desc: m.f1_points_legend_fp() },
    { abbr: "DNF", desc: m.f1_points_legend_dnf() },
  ];

  const items = isDrivers ? driverItems : constructorItems;

  return (
    <div className="rounded-lg border border-border bg-card/30">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 min-h-[44px] text-xs text-muted-foreground"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        <span>{m.f1_points_scoring_legend()}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-1">
          {items.map((item) => (
            <div key={item.abbr} className="flex items-baseline gap-1.5">
              <span className="font-mono text-xs font-bold text-primary shrink-0">
                {item.abbr}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {item.desc}
              </span>
            </div>
          ))}
          {isSprintWeekend && (
            <div className="col-span-2 mt-1 text-[10px] text-muted-foreground">
              {m.f1_points_sprint_note()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getValueColor(value: number) {
  if (value > 0) return "text-success";
  if (value < 0) return "text-destructive";
  return "text-muted-foreground";
}

function ScoreItem({ label, value }: { label: string; value: number }) {
  if (value === 0) return null;
  return (
    <span className={`font-mono text-xs ${getValueColor(value)}`}>
      {label} {value > 0 ? `+${value}` : value}
    </span>
  );
}

function MobileDriverScoreCard({
  driver: d,
  isSprintWeekend,
  season,
}: {
  driver: DriverFantasyResult;
  isSprintWeekend: boolean;
  season: number;
}) {
  const teamColor = getTeamColor(d.constructor?.slug ?? "");
  const rules = getScoringRules(season);

  // Calculate points breakdown
  const qualifyingPts = getQualifyingPoints(d.qualifyingPosition);
  const sprintPositionPts = d.sprintDnf ? 0 : getSprintPoints(d.sprintPosition);
  const sprintPgPts = d.sprintDnf ? 0 : d.sprintPositionsGained;
  const sprintOvPts = d.sprintOvertakes;
  const sprintFlPts = d.sprintFastestLap ? rules.fastestLapSprint : 0;
  const sprintDnfPts = d.sprintDnf ? rules.sprintDnfPenalty : 0;
  const racePositionPts = d.raceDnf ? 0 : getRacePoints(d.racePosition);
  const racePgPts = d.raceDnf ? 0 : d.positionsGained;
  const raceOvPts = d.overtakes;
  const raceFlPts = d.fastestLap ? rules.fastestLapRace : 0;
  const raceDdPts = d.driverOfTheDay ? rules.driverOfTheDay : 0;
  const raceDnfPts = d.raceDnf ? rules.raceDnfPenalty : 0;

  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="w-1 h-10 rounded-full shrink-0"
          style={{ backgroundColor: teamColor }}
        />
        <span
          className="font-mono font-bold text-xs px-1.5 py-0.5 rounded shrink-0 text-foreground"
          style={{
            borderColor: teamColor,
            borderWidth: "2px",
            borderStyle: "solid",
          }}
        >
          {d.code}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {d.driver?.fullName ?? d.code}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {d.constructor?.name}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span
            className={`font-mono font-bold text-lg ${getValueColor(d.totalPoints)}`}
          >
            {d.totalPoints}
          </span>
          <div className="font-mono text-xs text-muted-foreground">
            ${d.price.toFixed(1)}M
          </div>
        </div>
      </div>

      {/* Scoring breakdown */}
      <div className="px-4 pb-3 space-y-1.5">
        {/* Qualifying */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
            {m.f1_points_section_quali()}
          </span>
          <div className="flex flex-wrap gap-2">
            <ScoreItem label="POS" value={qualifyingPts} />
          </div>
        </div>

        {/* Sprint */}
        {isSprintWeekend && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
              {m.f1_points_section_sprint()}
            </span>
            <div className="flex flex-wrap gap-2">
              {d.sprintDnf ? (
                <span
                  className="font-mono text-xs font-bold text-red-500"
                  title={`${sprintDnfPts} ${m.f1_budget_pts_unit()}`}
                >
                  DNF
                </span>
              ) : (
                <ScoreItem label="POS" value={sprintPositionPts} />
              )}
              <ScoreItem label="PG" value={sprintPgPts} />
              <ScoreItem label="OV" value={sprintOvPts} />
              <ScoreItem label="FL" value={sprintFlPts} />
            </div>
          </div>
        )}

        {/* Race */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
            {m.f1_points_section_race()}
          </span>
          <div className="flex flex-wrap gap-2">
            {d.raceDnf ? (
              <span
                className="font-mono text-xs font-bold text-red-500"
                title={`${raceDnfPts} ${m.f1_budget_pts_unit()}`}
              >
                DNF
              </span>
            ) : (
              <ScoreItem label="POS" value={racePositionPts} />
            )}
            <ScoreItem label="PG" value={racePgPts} />
            <ScoreItem label="OV" value={raceOvPts} />
            <ScoreItem label="FL" value={raceFlPts} />
            <ScoreItem label="DD" value={raceDdPts} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileConstructorScoreCard({
  constructor: c,
  isSprintWeekend,
  season,
}: {
  constructor: ConstructorFantasyResult;
  isSprintWeekend: boolean;
  season: number;
}) {
  const teamColor = getTeamColor(c.constructor?.slug ?? "");

  // Calculate breakdown
  const breakdown = calculateConstructorFantasyBreakdown(
    {
      qualifyingPosition1: c.qualifyingPosition1,
      qualifyingPosition2: c.qualifyingPosition2,
      qualifyingDqPoints: c.qualifyingDqPoints,
      sprintPosition1: c.sprintPosition1,
      sprintPosition2: c.sprintPosition2,
      sprintDnf1: c.sprintDnf1,
      sprintDnf2: c.sprintDnf2,
      sprintPositionsGained: c.sprintPositionsGained,
      sprintOvertakes: c.sprintOvertakes,
      sprintFastestLap: c.sprintFastestLap,
      sprintDqPoints: c.sprintDqPoints,
      sprintDnfCount: c.sprintDnfCount,
      racePosition1: c.racePosition1,
      racePosition2: c.racePosition2,
      raceDnf1: c.raceDnf1,
      raceDnf2: c.raceDnf2,
      positionsGained: c.positionsGained,
      overtakes: c.overtakes,
      fastestLap: c.fastestLap,
      pitStopTimePoints: c.pitStopTimePoints,
      overallFastestPitStop: c.overallFastestPitStop,
      worldRecordPitStop: c.worldRecordPitStop,
      raceDqPoints: c.raceDqPoints,
      dnfCount: c.dnfCount,
    },
    isSprintWeekend,
    season,
  );

  const code =
    c.constructor?.code ??
    c.constructor?.slug?.slice(0, 3).toUpperCase() ??
    "???";

  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="w-1 h-10 rounded-full shrink-0"
          style={{ backgroundColor: teamColor }}
        />
        <span
          className="font-mono font-bold text-sm px-2 py-0.5 rounded text-foreground"
          style={{
            borderColor: teamColor,
            borderWidth: "2px",
            borderStyle: "solid",
          }}
        >
          {code}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground truncate">
            {c.constructor?.name}
          </div>
        </div>
        <div className="text-right shrink-0">
          <span
            className={`font-mono font-bold text-lg ${getValueColor(c.totalPoints)}`}
          >
            {c.totalPoints}
          </span>
          <div className="font-mono text-xs text-muted-foreground">
            ${c.price.toFixed(1)}M
          </div>
        </div>
      </div>

      {/* Scoring breakdown */}
      <div className="px-4 pb-3 space-y-1.5">
        {/* Qualifying */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
            {m.f1_points_section_quali()}
          </span>
          <div className="flex flex-wrap gap-2">
            <ScoreItem label="POS" value={breakdown.qualifyingPts} />
            <ScoreItem label="TW" value={breakdown.teamworkPts} />
            <ScoreItem label="DQ" value={breakdown.qualifyingDqPts} />
          </div>
        </div>

        {/* Sprint */}
        {isSprintWeekend && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
              {m.f1_points_section_sprint()}
            </span>
            <div className="flex flex-wrap gap-2">
              <ScoreItem label="POS" value={breakdown.sprintPosPts} />
              <ScoreItem label="PG" value={breakdown.sprintPgPts} />
              <ScoreItem label="OV" value={breakdown.sprintOvPts} />
              <ScoreItem label="FL" value={breakdown.sprintFlPts} />
            </div>
          </div>
        )}

        {/* Race */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-primary uppercase w-12 shrink-0">
            {m.f1_points_section_race()}
          </span>
          <div className="flex flex-wrap gap-2">
            <ScoreItem label="POS" value={breakdown.racePosPts} />
            <ScoreItem label="PG" value={breakdown.racePgPts} />
            <ScoreItem label="OV" value={breakdown.raceOvPts} />
            <ScoreItem label="FL" value={breakdown.raceFlPts} />
            <ScoreItem label="FP" value={breakdown.raceFpPts} />
            <ScoreItem label="DNF" value={breakdown.dnfPts} />
          </div>
        </div>
      </div>
    </div>
  );
}
